const LOCALE = 'en-US';
const UTC_TZ = 'UTC';

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

const cache = new Map<string, Intl.DateTimeFormat>();

function fmt(opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
    const key = JSON.stringify(opts);
    let f = cache.get(key);
    if (!f) {
        f = new Intl.DateTimeFormat(LOCALE, opts);
        cache.set(key, f);
    }
    return f;
}

export function tsDate(sec: number): Date {
    return new Date(sec * 1000);
}

export function localDate(d: Date): string {
    return fmt({ day: 'numeric', month: 'short', year: 'numeric' }).format(d);
}

export function localDateTime(d: Date): string {
    return fmt({
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).format(d);
}

export function localDayShort(d: Date): string {
    return fmt({ day: 'numeric', month: 'short' }).format(d);
}

export function utcDayShort(d: Date): string {
    return fmt({ timeZone: UTC_TZ, day: 'numeric', month: 'short' }).format(d);
}

export function utcDateTime(d: Date): string {
    return fmt({
        timeZone: UTC_TZ,
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).format(d);
}

export function localZone(): string {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
        return 'UTC';
    }
}

export function tickFormatter(spanMs: number): (d: Date) => string {
    if (spanMs > 200 * DAY_MS) {
        const f = fmt({ month: 'short', year: '2-digit' });
        return d => f.format(d);
    }
    if (spanMs > 3 * DAY_MS) {
        const f = fmt({ day: 'numeric', month: 'short' });
        return d => f.format(d);
    }
    const day = fmt({ day: 'numeric', month: 'short' });
    const time = fmt({ hour: '2-digit', minute: '2-digit', hour12: false });
    return d =>
        d.getHours() === 0 && d.getMinutes() === 0
            ? day.format(d)
            : time.format(d);
}

export function pct(bp: number, digits = 2): string {
    return (
        new Intl.NumberFormat(LOCALE, {
            minimumFractionDigits: digits,
            maximumFractionDigits: digits,
        }).format(bp / 100) + ' %'
    );
}

export function deltaLabel(bp: number): string {
    if (bp === 0) return 'Hold';
    const sign = bp < 0 ? '−' : '+';
    return sign + Math.abs(bp) + ' bps';
}

export function binLabel(bp: number, j: number, count: number): string {
    const base = deltaLabel(bp);
    if (j === count - 1 && bp > 0) return '≥ ' + base;
    if (j === 0 && bp < 0) return '≤ ' + base;
    return base;
}

export function deltaCompact(bp: number): string {
    const r = Math.round(bp);
    if (r === 0) return 'no change';
    const sign = r < 0 ? '−' : '+';
    return sign + Math.abs(r) + ' bps';
}

export function pctInt(pPerMille: number): string {
    return (
        new Intl.NumberFormat(LOCALE).format(Math.round(pPerMille / 10)) + ' %'
    );
}

export function countdownDays(fromSec: number, toSec: number): string {
    const days = Math.round((toSec - fromSec) / 86400);
    if (days <= 0) return 'today';
    if (days === 1) return 'tomorrow';
    return `in ${days} days`;
}

export function formatRange(range: readonly [Date, Date]): string {
    const span = range[1].getTime() - range[0].getTime();
    const crossesYear = range[0].getFullYear() !== range[1].getFullYear();
    const f =
        span > 2 * 365 * DAY_MS
            ? fmt({ month: 'short', year: 'numeric' })
            : span > 2 * DAY_MS
              ? fmt(
                    crossesYear
                        ? { month: 'short', day: 'numeric', year: 'numeric' }
                        : { month: 'short', day: 'numeric' }
                )
              : fmt({
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false,
                });
    return `${f.format(range[0])} – ${f.format(range[1])}`;
}
